<!-- eslint-disable vue/no-v-html -->
<template>
  <utd-modal-dialog ref="myAccountDialogRef"
                    :modal-classes="'contact-information-modal'"
                    :close-on-overlay-click="isMobileOnDesktop"
                    :close-on-escape="!isMobileOnDesktop"
                    @closed="handleClose()">
    <template #header>{{ $t('MYACCOUNT.CONTACT_INFORMATION') }}</template>

    <div class="contact-information-dialog">
      <div class="dialog__profile-info">
        <p class="ds1-utd-weight-bold"
           :class="showSubscriberNumber ? 'ds1-mb-0' : ''">{{ welcomeName }}</p>
        <p v-if="showSubscriberNumber" class="ds1-utd-weight-bold ds1-mt-0">
          Subscriber number: {{ contactFieldValue('accountNumber') }}
        </p>
      </div>
      <utd-notification v-if="submitSuccess && !isUnexpectedError"
                        id="contactInfoNotification"
                        class="contactNotificationStyle"
                        :notification-mode="C_ACCOUNT.MESSAGE_TYPE.SUCCESS">
        <template #title>Success</template>
        <div class="dialog__server-text">{{ submitMessage }}</div>
      </utd-notification>
      <utd-notification v-if="showErrorNotification"
                        id="unexpectedServerError"
                        class="contactNotificationStyle"
                        :notification-mode="'error'">
        <template #title>Error</template>
        <ul class="ds1-pl-3">
          <li>
            <p class="dialog__server-error-text">
              {{ $t(formErrorMessage) }}
            </p>
          </li>
          <li v-if="isUnexpectedError">
            <span v-html="$t('ERRORS.CONTACT_ON_ERROR')" />
          </li>
        </ul>
      </utd-notification>
      <utd-notification v-if="isShowEmailDisclaimer"
                        id="emailDisclaimer"
                        class="contactNotificationStyle"
                        :notification-mode="C_ACCOUNT.MESSAGE_TYPE.INFO">
        <template #title>{{ $t(C_ACCOUNT.FORM_MESSAGING.EMAIL_NOTIFICATION_TITLE) }}</template>
        <span class="dialog__server-text"
              v-html="$t(C_ACCOUNT.FORM_MESSAGING.EMAIL_NOTIFICATION_BODY(disclaimerEmail))" />
      </utd-notification>
      <utd-notification v-if="displayUserProfessionNotification"
                        id="practiceSettingDisclaimer"
                        class="contactNotificationStyle"
                        :notification-mode="C_ACCOUNT.MESSAGE_TYPE.INFO"
                        :has-close-notification="true"
                        @closed="close">
        <template #title>{{ $t(C_ACCOUNT.FORM_MESSAGING.FIELDS_NEED_INFO_TITLE) }}</template>
        <span class="dialog__server-text"
              v-html="$t(C_ACCOUNT.FORM_MESSAGING.FIELDS_NEED_INFO_BODY)" />
      </utd-notification>
      <form class="dialog__contact-form">
        <div class="dialog__contact-info-container">
          <fieldset v-if="isFieldVisible('email')"
                    class="dialog__fieldset wk-js2">
            <utd-text-input :label="$t('MYACCOUNT.CONTACT_EMAIL')"
                            :is-error="isEmailError"
                            :error-message="emailValidator"
                            :is-disabled="isEmailFieldDisabled"
                            input-id="emailInput"
                            class="my-account-email-input">
              <input id="emailInput"
                     v-model="fields.emailAddress.emailAddress"
                     class="wk-field-input"
                     :disabled="isEmailFieldDisabled"
                     @blur="toggleEmailDisclaimerForSsoEmail()">
            </utd-text-input>
          </fieldset>
          <span v-if="!isEmailVerified"
                class="dialog__email-verification-pill"
                role="status"
                aria-label="On-network access only">
            ON-NETWORK ACCESS ONLY
          </span>
          <p v-if="showEmailChangeVerificationNote"
             class="dialog__email-verification-note">
            You will need access to the new account to complete an email address change.
          </p>
          <fieldset v-if="isFieldVisible('phone1')"
                    class="dialog__fieldset">
            <utd-phone-input ref="phoneInputRef"
                             :input="phoneInput"
                             :options="phoneOptions"
                             :is-dirty-override="isDirty['phone1']"
                             required
                             @on-phone-type-change="handlePhoneType"
                             @on-phone-input-blur="handlePhoneBlur"
                             @on-phone-input="handlePhoneInput" />
          </fieldset>
          <fieldset v-if="isFieldVisible('country')"
                    class="dialog__fieldset wk-js2">
            <utd-select id="countryInput"
                        :options="myAccountCountries"
                        :selected="selectedIndices.country"
                        :label="$t('MYACCOUNT.CONTACT_COUNTRY')"
                        @utd-select="handleCountrySelect" />
          </fieldset>
          <fieldset v-if="isFieldVisible('address1')"
                    class="dialog__fieldset wk-js2">
            <utd-text-input :label="$t('MYACCOUNT.CONTACT_ADDRESS1')"
                            :is-error="!isAddressValid && isDirty.address"
                            :error-message="addressValidator"
                            :is-disabled="!contactField('address1').isEditable"
                            input-id="mailingAddress">
              <input id="mailingAddress"
                     v-model="fields.address.address1"
                     class="wk-field-input"
                     @blur="handleInputBlur('address')">
            </utd-text-input>
          </fieldset>
          <fieldset v-if="isFieldVisible('address2')"
                    class="dialog__fieldset wk-js2">
            <utd-text-input :label="$t('MYACCOUNT.CONTACT_ADDRESS2')"
                            :is-error="!isAddress2Valid && isDirty.address"
                            :error-message="address2Validator"
                            :optional="true"
                            :is-disabled="!contactField('address2').isEditable"
                            input-id="addressLine2">
              <input id="addressLine2"
                     v-model="fields.address.address2"
                     class="wk-field-input"
                     @blur="handleInputBlur('address2')">
            </utd-text-input>
          </fieldset>
          <fieldset v-if="!hideAddress3 && isFieldVisible('address3')"
                    class="dialog__fieldset wk-js2">
            <utd-text-input :label="$t('MYACCOUNT.CONTACT_ADDRESS3')"
                            :is-error="!isAddress3Valid && isDirty.address"
                            :error-message="address3Validator"
                            :optional="true"
                            :is-disabled="!contactField('address3').isEditable"
                            input-id="addressLine3">
              <input id="addressLine3"
                     v-model="fields.address.address3"
                     class="wk-field-input"
                     @blur="handleInputBlur('address3')">
            </utd-text-input>
          </fieldset>
          <div v-if="isFieldVisible('address3')"
               class="dialog__add-address-line">
            <utd-button class="dialog__add-address-line-btn"
                        button-style="text"
                        @click.prevent="addAddressLine">
              <span :class="{'wkce-icon-plus-circle': hideAddress3,
                             'wkce-icon-minus-circle': !hideAddress3}" />
              <span>{{ hideAddress3 ? 'Add' : 'Hide' }} address line</span>
            </utd-button>
          </div>
          <fieldset v-if="isFieldVisible('zip')"
                    class="dialog__fieldset wk-js2">
            <utd-text-input :label="$t('MYACCOUNT.CONTACT_ZIP')"
                            :is-error="!isZipCodeValid && isDirty.zip"
                            :optional="!isUsOrCanada"
                            :error-message="zipCodeValidator"
                            :is-disabled="!contactField('zip').isEditable"
                            input-id="zipCode">
              <input id="zipCode"
                     v-model="fields.address.zip"
                     class="wk-field-input"
                     @blur="fetchZipCodeData()">
            </utd-text-input>
          </fieldset>
          <fieldset v-if="isFieldVisible('city')"
                    class="dialog__fieldset wk-js2">
            <utd-select v-if="showCityDropdown"
                        id="citySelect"
                        :options="citiesList"
                        :selected="selectedIndices.city"
                        :label="$t('MYACCOUNT.CONTACT_CITY')"
                        @utd-select="handleCitySelect" />
            <utd-text-input v-else
                            :is-error="!isCityValid && isDirty.city"
                            :error-message="cityValidator"
                            :is-disabled="!contactField('city').isEditable"
                            :label="$t('MYACCOUNT.CONTACT_CITY')"
                            input-id="cityInput">
              <input id="cityInput"
                     v-model="fields.address.city"
                     class="wk-field-input"
                     @blur="handleInputBlur('city')">
            </utd-text-input>
          </fieldset>
          <fieldset v-if="isFieldVisible('state') && isUsOrCanada"
                    class="dialog__fieldset wk-js2">
            <utd-select id="stateSelect"
                        :options="filteredStates"
                        :selected="selectedIndices.state"
                        :label="$t('MYACCOUNT.CONTACT_STATE')"
                        @utd-select="handleStateSelect" />
          </fieldset>
        </div>
        <div v-if="hasUserProfessions" class="dialog__user-profession-container ds1-pv-3">
          <fieldset v-if="isFieldVisible('practiceSetting')"
                    class="dialog__fieldset wk-js2">
            <utd-select class="practiceSettingSelect"
                        :options="myAccountPracticeSettingOptions"
                        :selected="selectedIndices.practiceSetting"
                        :placeholder="$t('MYACCOUNT.SELECT_PRACTICE_SETTING')"
                        label="Practice Setting"
                        @utd-select="handleUserProfessionsSelect(
                          'practiceSetting',
                          $event,
                          myAccountPracticeSettingOptions)" />
          </fieldset>
          <fieldset v-if="isFieldVisible('practiceType')"
                    class="dialog__fieldset wk-js2">
            <utd-select class="practiceTypeSelect"
                        :options="myAccountpracticeTypeOptions"
                        :selected="selectedIndices.practiceType"
                        :placeholder="$t('MYACCOUNT.SELECT_ROLE')"
                        label="Role"
                        @utd-select="handleUserProfessionsSelect(
                          'practiceType',
                          $event,
                          myAccountpracticeTypeOptions)" />
          </fieldset>
          <fieldset v-if="isFieldVisible('specialty')"
                    class="dialog__fieldset wk-js2">
            <utd-select class="specialtySelect"
                        :options="myAccountSpecialtyOptions"
                        :selected="selectedIndices.specialty"
                        :placeholder="$t('MYACCOUNT.SELECT_SPECIALTY')"
                        label="Specialty"
                        @utd-select="handleUserProfessionsSelect(
                          'specialty',
                          $event,
                          myAccountSpecialtyOptions)" />
          </fieldset>
        </div>
      </form>
      <div class="privacy-policy">
        <p v-if="!isUccState" 
           class="ds1-utd-size-1" 
           v-html="$t('MYACCOUNT.CONTACT_PRIVACY_DISCLOSURE')" />
        <p v-else 
           class="ds1-utd-size-1" 
           v-html="$t('MYACCOUNT.CONTACT_PRIVACY_DISCLOSURE_UCC')" />
      </div>
    </div>

    <template #footer>
      <utd-button id="closeDialogButton"
                  button-style="ghost"
                  @click.prevent="closeDialog()">
        {{ submitSuccess ? 'Close' : 'Cancel' }}
      </utd-button>
      <utd-button v-if="!shouldShowSendCodeAction"
                  id="saveChangesButton"
                  :disabled="isSubmitDisabled"
                  @click.prevent="submitForm()">
        Save Changes
      </utd-button>
      <utd-button v-if="shouldShowSendCodeAction"
                  id="sendVerificationCodeButton"
                  :disabled="isSendCodeDisabled"
                  @click.prevent="sendVerificationCode()">
        Send verification code
      </utd-button>
    </template>
  </utd-modal-dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import UtdPhoneInput from '_acaSrc/components/shared/input/UtdPhoneInput.vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdModalDialog from '_acaSrc/components/shared/stdlib/UtdModalDialog.vue';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';
import UtdSelect from '_acaSrc/components/shared/input/UtdSelect.vue';
import {
    C_ACCOUNT,
    C_MY_ACCOUNT,
    emailRegex,
    maxContactFieldLength,
    maxEmailLength,
    VERIFY_EMAIL_RETURN_TO_PARAM,
    validMailingAddressRegex,
    validZipCodeRegex,
    ZIPCODE_FETCH_SUCCESS
} from '_acaSrc/utility/constants';
import { setQueryParamValue } from '_acaSrc/utility/http';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import PubSub from '_acaSrc/utility/PubSub';
import { getDocument, getWindow } from '_acaSrc/utility/DOM';
import {
    SET_CONTACT_INFO_FIELDS,
    SET_PENDING_CONTACT_VERIFICATION,
    SET_PENDING_EMAIL_VERIFICATION_FLOW
} from '_acaSrc/store/account.store';
import { SET_USER_EMAIL, SET_EMAIL_VERIFIED } from '_acaSrc/store/profile.store';
import zipLocationService from '_acaSrc/composables/zipLocationService.composable';

export default {
    components: {
        UtdModalDialog,
        UtdPhoneInput,
        UtdButton,
        UtdTextInput,
        UtdNotification,
        UtdSelect
    },
    props: {
        isSso: {
            type: Boolean,
            default: false
        },
        isEditMyProfileLink: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            C_MY_ACCOUNT,
            C_ACCOUNT,
            isSubmitting: false,
            isUnexpectedError: false,
            isFormFieldError: false,
            formErrorMessage: 'MYACCOUNT.UPDATE_INFORMATION_UNEXPECTED_ERROR',
            canResetForm: true,
            hideAddress3: true,
            oldEmailAddress: '',
            serverErrors: {
                address1: '',
                address2: '',
                address3: '',
                city: '',
                state: '',
                county: '',
                zip: '',
                country: '',
                email: '',
                phone1: '',
                practiceSetting: '',
                practiceType: '',
                specialty: ''
            },
            isDirty: {
                address: false,
                city: false,
                zip: false,
                email: false,
                phone1: false
            },
            fields: {
                address: {
                    address1: '',
                    address2: '',
                    address3: '',
                    city: '',
                    state: '',
                    county: '',
                    zip: '',
                    country: ''
                },
                emailAddress: {
                    emailAddress: ''
                },
                phone1: '',
                phone1Type: ''
            },
            userProfession: {
                practiceSetting: '',
                practiceType: '',
                specialty: ''
            },
            isPhoneValid: false,
            selectedIndices: {
                phone1Type: 0,
                city: 0,
                country: 0,
                state: 0,
                practiceSetting: null,
                practiceType: null,
                specialty: null
            },
            hasFormChanged: false,
            attemptLimitReached: false,
            filteredStates: [],
            citiesList: [],
            submitSuccess: false,
            submitMessage: '',
            formSubmitted: false,
            isShowEmailDisclaimer: false,
            disclaimerEmail: '',
            isInfoNotificationVisible: true
        };
    },
    computed: {
        ...mapGetters('device', [ 'isMobileOnDesktop' ]),
        ...mapGetters('feature', [
            'isTwoFactorEmailVerification',
            'isTwoFactorEmailVerificationAllowDefer'
        ]),
        ...mapGetters('app', [ 'isUccState', 'router' ]),
        ...mapGetters('profile', [
            'welcomeName',
            'isEmailVerified',
            'isOnNetwork'
        ]),
        ...mapGetters('account', [
            'myAccountContactInfo',
            'myAccountFormName',
            'myAccountStates',
            'myAccountCountries',
            'myAccountPracticeSettingOptions',
            'myAccountSpecialtyOptions',
            'myAccountpracticeTypeOptions',
            'hasUserProfessions',
            'pendingContactVerification'
        ]),
        showErrorNotification() {
            return this.isUnexpectedError || this.isFormFieldError;
        },
        phoneOptions() {
            return {
                showError: true,
                showSuccess: false,
                serverError: this.serverErrors.phone1,
                isDisabled: !this.contactField('phone1').isEditable,
                formSubmitted: this.formSubmitted
            };
        },
        isDomestic() {
            return this.fields.address.country === 'USA';
        },
        isUsOrCanada() {
            return this.fields.address.country === 'USA'
                || this.fields.address.country === 'CAN';
        },
        showSubscriberNumber() {
            return !this.contactField('accountNumber').isHidden
                && this.contactFieldValue('accountNumber') !== '';
        },
        hasEmailChanged() {
            return this.fields.emailAddress.emailAddress !== this.oldEmailAddress;
        },
        shouldShowSendCodeAction() {
            return this.hasEmailChanged
                && this.isTwoFactorEmailVerification
                && (!this.isOnNetwork
                    || !this.isTwoFactorEmailVerificationAllowDefer);
        },
        phoneInput() {
            return {
                inputSize: 'small',
                phoneValue: this.fields.phone1,
                phoneType: this.selectedIndices.phone1Type
            };
        },
        isSendCodeDisabled() {
            return this.isSubmitting
                || this.isUnexpectedError
                || this.emailValidator !== ''
                || !this.isFormValid;
        },
        isSubmitDisabled() {
            return this.isSubmitting
                || this.isUnexpectedError
                || this.attemptLimitReached
                || !this.isFormValid;
        },
        isEmailFieldDisabled() {
            const baseDisabled = !this.contactField('email').isEditable;
            const offNetworkAndNotVerified = !this.isOnNetwork && !this.isEmailVerified;
            return baseDisabled || offNetworkAndNotVerified;
        },
        showEmailChangeVerificationNote() {
            if (!this.isTwoFactorEmailVerification) {
                return false;
            }
            if (!this.isOnNetwork && !this.isEmailVerified) {
                return false;
            }
            if (this.isOnNetwork && this.isTwoFactorEmailVerificationAllowDefer) {
                return false;
            }
            return true;
        },
        cityValidator() {
            if (this.serverErrors.city) {
                return this.$t(this.serverErrors.city);
            }
            if (!this.fields.address.city.length > 0) {
                return this.$t('MYACCOUNT.EMPTY_FIELD');
            }
            const maxLength = this.contactField('city').maxLength || maxContactFieldLength;
            if (this.fields.address.city.length > maxLength) {
                return this.$t('MYACCOUNT.INVALID_FIELD_LENGTH', { maxLength });
            }
            if (!this.fields.address.city.match(validMailingAddressRegex)) {
                return this.$t('MYACCOUNT.INVALID_CITY');
            }
            return '';
        },
        zipCodeValidator() {
            if (this.serverErrors.zip) {
                return this.$t(this.serverErrors.zip);
            }
            if (!this.fields.address.zip.length > 0 && this.isUsOrCanada) {
                return this.$t('MYACCOUNT.EMPTY_FIELD');
            }
            const maxLength = this.contactField('zip').maxLength || maxContactFieldLength;
            if (this.fields.address.zip.length > maxLength) {
                return this.$t('MYACCOUNT.INVALID_FIELD_LENGTH', { maxLength });
            }
            if (!this.fields.address.zip.match(validZipCodeRegex)) {
                return this.$t('MYACCOUNT.INVALID_ZIPCODE');
            }
            return '';
        },
        addressValidator() {
            if (this.serverErrors.address1) {
                return this.$t(this.serverErrors.address1);
            }
            if (!this.fields.address.address1.length > 0) {
                return this.$t('MYACCOUNT.EMPTY_FIELD');
            }
            const maxLength = this.contactField('address1').maxLength || maxContactFieldLength;
            if (this.fields.address.address1.length > maxLength) {
                return this.$t('MYACCOUNT.INVALID_FIELD_LENGTH', { maxLength });
            }
            if (!this.fields.address.address1.match(validMailingAddressRegex)) {
                return this.$t('MYACCOUNT.INVALID_ADDRESS');
            }
            return '';
        },
        address2Validator() {
            if (this.serverErrors.address2) {
                return this.$t(this.serverErrors.address2);
            }
            const maxLength = this.contactField('address2').maxLength || maxContactFieldLength;
            if (this.fields.address.address2.length > maxLength) {
                return this.$t('MYACCOUNT.INVALID_FIELD_LENGTH', { maxLength });
            }
            if (!this.fields.address.address2.match(validMailingAddressRegex)) {
                return this.$t('MYACCOUNT.INVALID_ADDRESS');
            }
            return '';
        },
        address3Validator() {
            if (this.serverErrors.address3) {
                return this.$t(this.serverErrors.address3);
            }
            const maxLength = this.contactField('address2').maxLength || maxContactFieldLength;
            if (this.fields.address.address3.length > maxLength) {
                return this.$t('MYACCOUNT.INVALID_FIELD_LENGTH', { maxLength });
            }
            if (!this.fields.address.address3.match(validMailingAddressRegex)) {
                return this.$t('MYACCOUNT.INVALID_ADDRESS');
            }
            return '';
        },
        isEmailError() {
            return !this.isEmailValid && this.isDirty.email;
        },
        emailValidator() {
            if (this.serverErrors.email) {
                return this.$t(this.serverErrors.email);
            }
            if (!this.fields.emailAddress.emailAddress.length > 0) {
                return this.$t('MYACCOUNT.EMPTY_FIELD');
            }
            if (this.fields.emailAddress.emailAddress.length > maxEmailLength) {
                return this.$t('MYACCOUNT.INVALID_FIELD_LENGTH', { maxLength: maxEmailLength });
            }
            if (!this.fields.emailAddress.emailAddress.match(emailRegex)) {
                return this.$t('MYACCOUNT.INVALID_EMAIL');
            }
            return '';
        },
        isAddressValid() {
            return !this.isFieldVisible('address1')
                || (this.addressValidator === '' && !this.serverErrors.address1);
        },
        isAddress2Valid() {
            return !this.isFieldVisible('address2')
                || (this.address2Validator === '' && !this.serverErrors.address2);
        },
        isAddress3Valid() {
            return !this.isFieldVisible('address3')
                || (this.address3Validator === '' && !this.serverErrors.address3);
        },
        isEmailValid() {
            return !this.isFieldVisible('email')
                || (this.emailValidator === '' && !this.serverErrors.email);
        },
        isZipCodeValid() {
            return !this.isFieldVisible('zip')
                || (this.zipCodeValidator === '' && !this.serverErrors.zip);
        },
        isCityValid() {
            return !this.isFieldVisible('city')
                || (this.cityValidator === '' && !this.serverErrors.city);
        },
        isStateValid() {
            return !this.isFieldVisible('state')
                || ((this.fields.address.state !== '' || !this.isDomestic)
                    && !this.serverErrors.state);
        },
        isPracticeSettingValid() {
            return this.isUserProfessionValid('practiceSetting');
        },
        ispracticeTypeValid() {
            return this.isUserProfessionValid('practiceType');
        },
        isSpecialtyValid() {
            return this.isUserProfessionValid('specialty');
        },
        showCityDropdown() {
            return this.isDomestic
                && this.citiesList.length > 0
                && this.isZipCodeValid;
        },
        isPhoneFieldValid() {
            return !this.isFieldVisible('phone1')
                || (this.isPhoneValid && !this.serverErrors.phone1);
        },
        // eslint-disable-next-line complexity
        isFormValid() {
            return this.isAddressValid
                && this.isAddress2Valid
                && this.isAddress3Valid
                && this.isEmailValid
                && this.isZipCodeValid
                && this.isPhoneFieldValid
                && this.isCityValid
                && this.isStateValid
                && this.isPracticeSettingValid
                && this.ispracticeTypeValid
                && this.isSpecialtyValid;
        },
        displayUserProfessionNotification() {
            return this.hasUserProfessions
                && (this.userProfession.practiceSetting
                === C_ACCOUNT.FORM_MESSAGING.PRACTICE_SETTING_EMPTY_VALUE
                || this.userProfession.practiceType
                === C_ACCOUNT.FORM_MESSAGING.PRACTICE_SETTING_EMPTY_VALUE
                || this.userProfession.specialty
                === C_ACCOUNT.FORM_MESSAGING.PRACTICE_SETTING_EMPTY_VALUE)
                && !this.isEditMyProfileLink
                && this.isInfoNotificationVisible;
        }
    },
    mounted() {
        if (!(this.myAccountStates.length > 0
        && this.myAccountCountries.length > 0
        && this.myAccountPracticeSettingOptions.length > 0
        && this.myAccountSpecialtyOptions.length > 0
        && this.myAccountpracticeTypeOptions.length > 0
        )) {
            this.handleUnexpectedError('MYACCOUNT.CONTACT_INFORMATION_SETUP_ERROR', false);
        }
        this.setup();
    },
    methods: {
        ...mapMutations('account', [
            SET_CONTACT_INFO_FIELDS,
            SET_PENDING_CONTACT_VERIFICATION,
            SET_PENDING_EMAIL_VERIFICATION_FLOW
        ]
        ),
        ...mapActions('account', [
            'getMyAccountInfo'
        ]),
        setup() {
            this.filteredStates = this.myAccountStates;
            this.prefillContactFields();
            this.prefillUserProfession();
            this.setPreFilledFields();
            this.setAllFieldsDirty(false);
            this.oldEmailAddress = this.contactFieldValue('email');
        },
        contactField(field) {
            return this.myAccountContactInfo[field] || {};
        },
        contactFieldValue(field) {
            return this.myAccountContactInfo[field]?.value || '';
        },
        isFieldVisible(field) {
            const fieldConfig = this.contactField(field);
            const hasFieldData = Object.keys(fieldConfig).length > 0;
            if (this.myAccountFormName === C_MY_ACCOUNT.FORM_NAME) {
                const isZipFieldHidden = field === 'zip' && !this.isUsOrCanada;
                const isCityFieldHidden = field === 'city' && this.isUsOrCanada;
                if (isZipFieldHidden || isCityFieldHidden) {
                    return false;
                }
            }
            return hasFieldData && !fieldConfig.isHidden;
        },
        setFormChanged() {
            this.hasFormChanged = true;
            this.handleFormReset();
        },
        resetNotificationState() {
            this.isUnexpectedError = false;
            this.isFormFieldError = false;
        },
        resetErrors() {
            this.resetNotificationState();
            this.serverErrors = {
                address1: '',
                address2: '',
                address3: '',
                city: '',
                state: '',
                county: '',
                zip: '',
                country: '',
                email: '',
                phone1: '',
                practiceSetting: '',
                practiceType: '',
                specialty: ''
            };
        },
        handleCountrySelect(value) {
            if (value < 0) {
                return;
            }
            if (this.fields.address.country !== this.myAccountCountries[value].value) {
                this.fields.address.zip = '';
                this.fields.address.city = '';
                this.fields.address.state = '';
                this.selectedIndices.city = -1;
                this.selectedIndices.state = 0;
            }
            if (this.isUsOrCanada) {
                this.filteredStates = this.myAccountStates;
            }
            this.fields.address.country = this.myAccountCountries[value].value;
            this.selectedIndices.country = value;
            this.handleInputBlur('zip');
        },
        handleCitySelect(value) {
            if (value < 0) {
                return;
            }
            this.fields.address.city = this.citiesList[value].label;
            this.selectedIndices.city = value;
            this.handleInputBlur('city');
        },
        handleStateSelect(value) {
            if (value < 0) {
                return;
            }
            this.fields.address.state = this.myAccountStates[value].value;
            this.selectedIndices.state = value;
            this.handleInputBlur('state');
        },
        handleUserProfessionsSelect(key, value, list) {
            if (value < 0) {
                return;
            }
            this.userProfession[key] = list[value].value;
            this.selectedIndices[key] = value;
            this.setFormChanged();
        },
        prefillContactFields() {
            this.fields = {
                address: {
                    address1: this.contactFieldValue('address1'),
                    address2: this.contactFieldValue('address2'),
                    address3: this.contactFieldValue('address3'),
                    city: this.contactFieldValue('city'),
                    state: this.contactFieldValue('state'),
                    county: this.contactFieldValue('county'),
                    zip: this.contactFieldValue('zip'),
                    country: this.contactFieldValue('country')
                },
                emailAddress: {
                    emailAddress: this.contactFieldValue('email')
                },
                phone1: this.contactFieldValue('phone1')
            };
        },
        prefillUserProfession() {
            this.userProfession = {
                practiceSetting: this.contactFieldValue('practiceSetting'),
                practiceType: this.contactFieldValue('practiceType'),
                specialty: this.contactFieldValue('specialty')
            };
        },
        setPrefilledPhone1Type() {
            let phone1Type = this.contactFieldValue('phone1Type');
            if (!phone1Type && !this.contactFieldValue('phone2')) {
                phone1Type = this.contactFieldValue('phone2Type');
            }
            this.fields.phone1Type = phone1Type || 'Work';
        },
        setPrefilledCountry() {
            if (this.fields.address.country) {
                for (let i = 0; i < this.myAccountCountries.length; i++) {
                    const country = this.myAccountCountries[i];
                    if (country.value === this.fields.address.country) {
                        this.selectedIndices.country = i;
                        break;
                    }
                }
            }
        },
        setPrefilledState() {
            if (this.fields.address.state) {
                for (let i = 0; i < this.myAccountStates.length; i++) {
                    const state = this.myAccountStates[i];
                    if (state.value === this.fields.address.state) {
                        this.selectedIndices.state = i;
                        break;
                    }
                }
            }
        },
        setPrefilledCounty() {
            if (!this.isDomestic) {
                return;
            }
            if (!this.submitSuccess && !this.fields.address.county && this.fields.address.zip) {
                this.fetchZipCodeData();
            }
        },
        setPrefilledCity() {
            if (!this.fields.address.city) {
                return;
            }
            this.citiesList = [{
                label: this.fields.address.city,
                value: 0
            }];
        },
        setPrefilledPracticeSetting() {
            this.setPrefilledUserProfession('practiceSetting',
                this.myAccountPracticeSettingOptions);
        },
        setPrefilledpracticeType() {
            this.setPrefilledUserProfession('practiceType',
                this.myAccountpracticeTypeOptions);
        },
        setPrefilledSpecialty() {
            this.setPrefilledUserProfession('specialty',
                this.myAccountSpecialtyOptions);
        },
        setPrefilledUserProfession(label, list) {
            if (this.userProfession[label]) {
                for (let i = 0; i < list.length; i++) {
                    const option = list[i];
                    if (option.value === this.userProfession[label]) {
                        this.selectedIndices[label] = i;
                        break;
                    }
                }
            }
        },
        setPreFilledFields() {
            this.setPrefilledCountry();
            this.setPrefilledState();
            this.setPrefilledCounty();
            this.setPrefilledCity();
            this.setPrefilledPhone1Type();
            this.setPrefilledPracticeSetting();
            this.setPrefilledpracticeType();
            this.setPrefilledSpecialty();
            if (this.fields.phone1Type) {
                this.selectedIndices.phone1Type = C_MY_ACCOUNT.PHONE_TYPES
                    .indexOf(this.fields.phone1Type.toLowerCase());
            }
            if (this.fields.phone1) {
                this.setDirtyField('phone1');
                this.isPhoneValid = true;
            }
            if (this.fields.address.address3) {
                this.hideAddress3 = false;
            }
        },
        addAddressLine() {
            this.hideAddress3 = !this.hideAddress3;
        },
        handlePhoneInput(value) {
            this.fields.phone1 = value;
            this.handleInputBlur('phone1');
            this.setFormChanged();
        },
        handlePhoneBlur(isValid) {
            this.isPhoneValid = isValid;
        },
        handlePhoneType(value) {
            this.fields.phone1Type = value;
            this.setFormChanged();
        },
        handleInputBlur(field) {
            this.setDirtyField(field);
            this.setServerError(field, '');
            this.setFormChanged();
        },
        setDirtyField(field) {
            this.isDirty[field] = true;
        },
        setAllFieldsDirty(setFormSubmitted = true) {
            this.isDirty = {
                address: true,
                city: true,
                zip: true,
                email: true,
                phone1: true
            };
            if (setFormSubmitted) {
                this.formSubmitted = true;
            }
        },
        setServerError(field, error) {
            this.serverErrors[field] = error;
        },
        open() {
            this.$refs.myAccountDialogRef.open();
        },
        restoreFromPendingContactVerification() {
            const pending = this.pendingContactVerification;
            if (!pending || !pending.contactInfo) {
                return;
            }
            this.fields = JSON.parse(JSON.stringify(pending.contactInfo));
            this.userProfession = JSON.parse(JSON.stringify(pending.userProfession || {}));
            this.setPreFilledFields();
        },
        openFromEmailVerificationBack() {
            this.restoreFromPendingContactVerification();
            this.$refs.myAccountDialogRef.open();
        },
        async handlePracticeSettingScroll() {
            await this.$nextTick();
            const practiceSettingElem = getDocument().querySelector('.practiceSettingSelect');
            if (practiceSettingElem) {
                practiceSettingElem.scrollIntoView();
            }
        },
        handleFormReset() {
            if (!this.canResetForm) {
                return;
            }
            this.submitSuccess = false;
            this.submitMessage = '';
            this.resetNotificationState();
        },
        closeDialog() {
            new PubSub().publish('wkutd.closeMyAccountDialog');
            this.$refs.myAccountDialogRef.close();
            this.clearErrors();
            this.handleFormReset();
        },
        handleClose() {
            this.clearErrors();
            this.handleFormReset();
            new PubSub().publish('wkutd.closeMyAccountDialog');
        },
        clearErrors() {
            if (this.formErrorMessage === 'MYACCOUNT.CONTACT_INFORMATION_SETUP_ERROR') {
                return;
            }
            this.canResetForm = true;
            this.hasFormChanged = false;
        },
        setZipCodeError() {
            if (!this.isDomestic) {
                return;
            }
            this.setServerError('zip', this.$t('MYACCOUNT.INVALID_ZIPCODE'));
        },
        fetchContactForm() {
            this.getMyAccountInfo();
            this.setup();
        },
        handleSubmitSuccess() {
            this.attemptLimitReached = false;
            this.formSubmitted = false;
            this.hasFormChanged = false;
            this.oldEmailAddress = this.contactFieldValue('email')
                || this.fields.emailAddress.emailAddress;
        },
        async handleContactFormResponse(res) {
            if (!res.isSuccess) {
                this.handleServerErrors(res);
                return false;
            }
            return true;
        },
        async fetchZipCodeData() {
            if (this.formErrorMessage === 'MYACCOUNT.CONTACT_INFORMATION_SETUP_ERROR') {
                return;
            }
            this.handleInputBlur('zip');
            const userZipCode = this.fields.address.zip;
            if (!userZipCode || !this.isDomestic) {
                this.canResetForm = true;
                this.isUnexpectedError = false;
                return;
            }
            try {
                const {
                    status,
                    citiesList,
                    selectedState,
                    county
                } = await zipLocationService().fetchCityStateFromZip(userZipCode);
                if (status === ZIPCODE_FETCH_SUCCESS) {
                    this.citiesList = citiesList;
                    this.filteredStates = this.myAccountStates.filter(
                        state => state.value === selectedState);
                    this.fields.address.city = this.citiesList[0].label;
                    this.fields.address.state = this.filteredStates[0].value;
                    this.fields.address.county = county;
                    this.selectedIndices.state = 0;
                    this.selectedIndices.city = 0;
                    this.setServerError('zip', '');
                }
                else {
                    this.setZipCodeError();
                }
            }
            catch {
                this.handleUnexpectedError(
                    'MYACCOUNT.VALIDATE_INFORMATION_UNEXPECTED_ERROR',
                    false,
                    true);
            }
        },
        async submitForm() {
            this.isInfoNotificationVisible = false;
            this.setAllFieldsDirty();
            if (!this.isFormValid) {
                return;
            }
            if (this.handleSubmitFormBlockedFlow()) {
                return;
            }
            const res = await this.submitContactInfo();
            if (!res) {
                return;
            }
            const contactInfoResponse = res.contactInfoResponse;
            this.handleFormScroll();
            if (contactInfoResponse) {
                this.submitSuccess = contactInfoResponse.isSuccess;
                this.submitMessage = contactInfoResponse.statusMessage;
                await this.handleContactFormResponse(contactInfoResponse);
            }
            if (this.submitSuccess) {
                const shouldRedirectToEmailVerification
                    = this.shouldRedirectToEmailVerificationAfterSave();
                this[SET_CONTACT_INFO_FIELDS](contactInfoResponse.formFields);
                this.syncSavedEmailToProfile(contactInfoResponse);
                this.handleSubmitSuccess();
                this.resetErrors();
                if (shouldRedirectToEmailVerification) {
                    this[SET_PENDING_EMAIL_VERIFICATION_FLOW](true);
                    this.redirectToEmailVerificationPrompt();
                }
            }
            else {
                this.isFormFieldError = true;
                this.formErrorMessage = 'ERRORS.SUBMISSION_GENERAL_ERROR';
            }
        },
        handleSubmitFormBlockedFlow() {
            if (!this.shouldBlockSaveUntilEmailVerified()) {
                return false;
            }
            this.queueContactVerificationAndRedirect();
            return true;
        },
        shouldBlockSaveUntilEmailVerified() {
            if (!this.isTwoFactorEmailVerification) {
                return false;
            }
            if (!this.isOnNetwork) {
                return this.hasEmailChanged;
            }
            if (this.isTwoFactorEmailVerificationAllowDefer) {
                return false;
            }
            return this.shouldRedirectToEmailVerificationAfterSave();
        },
        getSavedEmailFromResponse(contactInfoResponse) {
            const formFields = contactInfoResponse?.formFields;
            if (!Array.isArray(formFields)) {
                return this.fields.emailAddress.emailAddress;
            }
            const emailField = formFields.find(field => field?.name === 'emailAddress');
            return emailField?.emailAddress || emailField?.value
                || this.fields.emailAddress.emailAddress;
        },
        syncSavedEmailToProfile(contactInfoResponse) {
            const newEmailAddress = this.getSavedEmailFromResponse(contactInfoResponse);
            if (newEmailAddress === this.oldEmailAddress) {
                return;
            }
            this.$store.commit(`profile/${SET_USER_EMAIL}`, newEmailAddress);
            if (this.isTwoFactorEmailVerification && this.isTwoFactorEmailVerificationAllowDefer) {
                this.$store.commit(`profile/${SET_EMAIL_VERIFIED}`, false);
            }
            this.oldEmailAddress = newEmailAddress;
        },
        stashPendingContactVerification(verificationCodeSent = false) {
            this[SET_PENDING_CONTACT_VERIFICATION]({
                contactInfo: this.fields,
                userProfession: this.userProfession,
                verificationCodeSent,
                disableSendingEmail: !!Object.keys(this.contactField('disableSendingEmail')).length
                    && !this.contactField('disableSendingEmail').isHidden
            });
        },
        queueContactVerificationAndRedirect() {
            this.stashPendingContactVerification();
            this.redirectToEmailVerificationPrompt();
        },
        shouldRedirectToEmailVerificationAfterSave() {
            if (!this.isTwoFactorEmailVerification) {
                return false;
            }
            if (!this.isOnNetwork) {
                return this.hasEmailChanged;
            }
            return this.hasEmailChanged || !this.isEmailVerified;
        },
        getVerifyEmailRedirectUrl() {
            const { pathname, search, hash } = getWindow().location;
            const returnTo = `${pathname}${search}${hash}`;
            return setQueryParamValue(
                '/login-register/verify-email',
                VERIFY_EMAIL_RETURN_TO_PARAM,
                returnTo
            );
        },
        redirectToEmailVerificationPrompt() {
            this.closeDialog();
            new PubSub().publish('wkutd.closeMyAccountSidebar');
            this.router.url(this.getVerifyEmailRedirectUrl());
        },
        async toggleEmailDisclaimerForSsoEmail() {
            if (!this.isSso || !this.isEmailValid) {
                this.handleInputBlur('email');
                return;
            }

            try {
                const data = await utdRest('wayf/institutions/disclaimer-email', {
                    emailDomain: this.getContactEmailDomain()
                });
                if (data && data.data && data.data.trim() !== '') {
                    this.disclaimerEmail = data.data;
                    this.isShowEmailDisclaimer = true;
                }
                else {
                    this.disclaimerEmail = '';
                    this.isShowEmailDisclaimer = false;
                }
            }
            catch {
                // do nothing
            }
            finally {
                this.handleInputBlur('email');
            }
        },
        getContactEmailDomain() {
            const email = this.fields.emailAddress.emailAddress;
            let emailDomain = '';
            const domainIndex = email.indexOf('@');
            if (domainIndex >= 0) {
                emailDomain = email.substring(domainIndex + 1);
            }
            return emailDomain;
        },
        async submitContactInfo() {
            await this.$nextTick();
            if (!this.isFormValid) {
                return;
            }
            try {
                this.isFormFieldError = false;
                this.isUnexpectedError = false;
                this.isSubmitting = true;
                const data = await utdRest('profile/submitContact', {
                    contactInfo: this.fields,
                    userProfession: this.userProfession
                });
                const res = data.data || data;
                this.isSubmitting = false;
                return res;
            }
            catch(error) {
                this.handleSubmitContactInfoError(error);
            }
        },
        handleSubmitContactInfoError(error) {
            const emailError = error?.data?.errors?.find(
                fieldError => fieldError?.fields?.includes('original_email_verification'));
            const emailErrorMessage =
                C_ACCOUNT.FORM_MESSAGING.CONTACT_EMAIL_CHANGE_ERRORS[emailError?.utdStatus];
            if (emailErrorMessage) {
                this.setServerError('email', emailErrorMessage);
                this.isFormFieldError = false;
                this.isUnexpectedError = false;
                this.submitSuccess = false;
                this.isSubmitting = false;
                this.handleFormScroll();
                return;
            }
            const canResetForm = true;
            const shouldScrollToTop = true;
            this.handleUnexpectedError(
                'MYACCOUNT.UPDATE_INFORMATION_UNEXPECTED_ERROR',
                canResetForm,
                shouldScrollToTop);
        },
        getSendVerificationPayload() {
            return {
                contactInfo: this.fields,
                userProfession: this.userProfession
            };
        },
        prepareSendVerificationRequest() {
            this.isSubmitting = true;
            this.isFormFieldError = false;
            this.isUnexpectedError = false;
            this.formErrorMessage = 'MYACCOUNT.UPDATE_INFORMATION_UNEXPECTED_ERROR';
            this.resetErrors();
        },
        async requestSendVerificationCode() {
            const data = await utdRest('myAccount/sendVerificationCode',
                this.getSendVerificationPayload());
            return data?.data || data;
        },
        hasSendVerificationValidationErrors(response) {
            const hasFormFieldErrors = Array.isArray(response?.formFields)
                && response.formFields.some(field => field?.errorMessage);
            return response?.isSuccess === false
                || hasFormFieldErrors
                || !!response?.errorMessage;
        },
        handleSendVerificationValidationErrors(response) {
            if (response?.errorMessage && !Array.isArray(response?.formFields)) {
                this.setServerError('email', response.errorMessage);
            }
            this.handleServerErrors(response || {});
            this.isFormFieldError = true;
            this.handleFormScroll();
        },
        async sendVerificationCode() {
            if (this.isSendCodeDisabled) {
                return;
            }
            this.prepareSendVerificationRequest();
            try {
                const response = await this.requestSendVerificationCode();
                if (this.hasSendVerificationValidationErrors(response)) {
                    this.handleSendVerificationValidationErrors(response);
                    return;
                }
                this.stashPendingContactVerification(true);
                this.redirectToEmailVerificationPrompt();
            }
            catch {
                this.handleUnexpectedError(
                    'MYACCOUNT.UPDATE_INFORMATION_UNEXPECTED_ERROR',
                    true,
                    true);
            }
            finally {
                this.isSubmitting = false;
            }
        },
        handleFormScroll() {
            const dialogContainer = getWindow().document.querySelector('.utd-dialog-content');
            if (dialogContainer !== null) {
                dialogContainer.scrollTop = 0;
            }
        },
        handleServerErrors(contactInfoResponse) {
            if (Array.isArray(contactInfoResponse.formFields)) {
                contactInfoResponse.formFields.forEach(field => {
                    if (field.errorMessage && field.name) {
                        this.setServerError(field.name, field.errorMessage);
                    }
                });
            }
        },
        handleUnexpectedError(message, canResetForm, isHandleScroll = false) {
            this.canResetForm = canResetForm;
            this.formErrorMessage = message;
            this.isUnexpectedError = true;
            this.submitSuccess = false;
            this.isSubmitting = false;
            if (isHandleScroll) {
                this.handleFormScroll();
            }
        },
        close(options = { doEmit: true }) {
            this.isInfoNotificationVisible = false;
            if (options.doEmit) {
                this.$emit('closed');
            }
        },
        isUserProfessionValid(label) {
            if (!this.hasUserProfessions || !this.isFieldVisible(label)) {
                return true;
            }
            return this.userProfession[label] !== ''
            && this.userProfession[label]
            !== C_ACCOUNT.FORM_MESSAGING.PRACTICE_SETTING_EMPTY_VALUE
            && !this.serverErrors[label];
        }
    }
};
</script>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';
@DS1-UTD-DIALOG-OUTLINE-COLOR: @WKCE-WHITE;

.contact-information-modal {
  .utd-modal-content {
    .utd-dialog-frame {
      .ds1-mb-0();
      min-width: 0;
      max-width: 390px;
    }

    .utd-dialog-content {
      .ds1-pa-2();
    }

    .utd-dialog-footer .utd-button {
      .ds1-ml-2();
    }

    .privacy-policy {
      padding-left: 12px;
    }
  }
}

#emailDisclaimer, #practiceSettingDisclaimer {
  .email-notification-content, .fields-notification-content {
    padding-left: 20px;
  }

  #emailDisclaimerMessage {
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
  }

  .utd-notification-title {
    font-weight: 600;
  }

  b {
    font-weight: 600;
  }
}

.dialog__user-profession-container {
    padding-left: 12px;
    padding-right: 12px;
    background-color: @WKCE-BLUE-TINT6;

    .specialtySelect {
        .ds1-mb-0();
    }
}

@media screen and (min-width: 768px) {
  .contact-information-modal {
    .utd-modal-content {
      width: 390px;
    }
  }
}
</style>


<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.dialog__add-address-line {
  .ds1-mb-2();
  display: flex;
  align-items: center;
}

.dialog__add-address-line-btn {
  .ds1-utd-weight-600();
  .ds1-pa-0();
  font-size: 14px;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }

  .wkce-icon-minus-circle,
  .wkce-icon-plus-circle {
    .ds1-utd-size-3();
    margin-right: 6px;
  }
}

.dialog__profile-info {
  .ds1-utd-size-2();
  overflow-wrap: break-word;
}

.dialog__email-verification-note {
  .ds1-utd-size-2();
}

.dialog__email-changed-error {
  .ds1-utd-size-2();
  color: @DS1-UTD-ERROR;
}

.dialog__email-verification-pill {
  .ds1-flex-vertical-center();
  .ds1-ph-1();
  .ds1-mb-2();
  .ds1-utd-weight-500();
  height: 16px;
  border-radius: 8px;
  background: @WKCE-BLUE-TINT3;
  font-size: 11px;
  line-height: 12px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  width: fit-content;
}

.dialog__verification-code-error {
  .ds1-utd-size-2();
  color: @DS1-UTD-ERROR;
}

.my-account-email-input {
  .ds1-mb-2();
}

.dialog__contact-info-container {
    padding: 12px;
}

.contactNotificationStyle {
    .ds1-mt-0();
    .ds1-mb-1();
}

.practiceSettingSelect,
.practiceTypeSelect,
.specialtySelect {
    :deep(select) {
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}
</style>
